import { useState } from 'react';
import {
  CreditCard, Settings, HelpCircle, LogOut,
  Star, Shield, Receipt, Users, Truck,
  ChevronRight, Bell
} from 'lucide-react';

const MenuItem = ({ icon: Icon, label, sub, badge, onClick }) => (
  <div onClick={onClick} className="flex justify-between items-center py-3.5 px-4 active:bg-slate-50 cursor-pointer hover:bg-slate-50 transition-colors">
    <div className="flex items-center gap-3 text-slate-700">
      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
        <Icon size={18} className="text-slate-600" />
      </div>
      <span className="text-sm font-medium">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      {sub && <span className="text-xs text-slate-400">{sub}</span>}
      {badge && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{badge}</span>}
      <ChevronRight size={16} className="text-slate-300" />
    </div>
  </div>
);

const StatItem = ({ value, label, prefix, suffix, color }) => (
  <div className="text-center">
    <div className={`font-bold text-xl mb-1 ${color || 'text-slate-800'}`}>
      {prefix}{value}{suffix}
    </div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);

export default function ProfilePage({ onManageBankCards, onRealNameAuth, onViewInvoiceRecords, onManageSuppliers }) {
  const [userRole, setUserRole] = useState('supplier');

  const supplierStats = {
    totalOrders: 12,
    monthIncome: '8.6万',
    totalIncome: '52.3万',
    totalWeight: '186吨',
    invoiceQuota: '68.5万',
    invoiceUsed: 68.5,
    invoiceLimit: 500
  };

  const organizerStats = {
    totalOrders: 8,
    monthAmount: '23.5万',
    suppliers: 3,
    totalWeight: '320吨'
  };

  return (
    <div className="bg-slate-50 min-h-full pb-4">
      {/* 页面头部 - 简洁白色风格 */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-4 py-4">
          {/* 用户信息 */}
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-md">
              张
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-800">张三</h2>
              <div className="text-slate-500 text-sm mb-1">138****8888</div>
              <div className="flex items-center gap-1 text-xs text-blue-600">
                <Star size={12} className="fill-blue-500" />
                <span>已实名认证</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors">
                <Bell size={20} />
              </button>
              <button className="p-2 text-slate-400 hover:bg-slate-100 rounded-full transition-colors">
                <Settings size={20} />
              </button>
            </div>
          </div>

          {/* 角色切换 */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setUserRole('supplier')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                userRole === 'supplier'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500'
              }`}
            >
              实供人
            </button>
            <button
              onClick={() => setUserRole('organizer')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${
                userRole === 'organizer'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500'
              }`}
            >
              组织者
            </button>
          </div>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="px-4 pt-4 space-y-4">
        {userRole === 'supplier' ? (
          <>
            <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <Truck size={16} className="text-blue-500" />
                <span className="text-sm font-medium text-slate-700">送货统计</span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <StatItem value={supplierStats.totalOrders} label="累计订单" />
                <StatItem value={supplierStats.monthIncome} label="本月收入" color="text-red-500" />
                <StatItem value={supplierStats.totalIncome} label="累计收入" />
                <StatItem value={supplierStats.totalWeight} label="送货总量" />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Receipt size={16} className="text-amber-500" />
                  <span className="text-sm font-medium text-slate-700">今年反向开票额度</span>
                </div>
                <span className="text-xs text-slate-400">年度限额500万</span>
              </div>
              <div className="mb-3">
                <div className="flex justify-between items-end mb-1.5">
                  <span className="text-2xl font-bold text-slate-800">{supplierStats.invoiceQuota}</span>
                  <span className="text-sm text-slate-500">/ 500万</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 rounded-full transition-all duration-300"
                    style={{ width: `${(supplierStats.invoiceUsed / supplierStats.invoiceLimit) * 100}%` }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2.5 py-1.5 rounded-lg">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>超过限额后需办理经营主体登记，自行开具发票</span>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-5 border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <Users size={16} className="text-blue-500" />
              <span className="text-sm font-medium text-slate-700">组织统计</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <StatItem value={organizerStats.suppliers} label="关联实供人" />
              <StatItem value={organizerStats.totalOrders} label="累计订单" />
              <StatItem value={organizerStats.monthAmount} label="本月交易额" color="text-red-500" />
              <StatItem value={organizerStats.totalWeight} label="送货总量" />
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-200">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <span className="text-xs font-medium text-slate-500">我的服务</span>
          </div>
          <div className="divide-y divide-slate-100">
            <MenuItem icon={CreditCard} label="我的银行卡" sub="3张" onClick={onManageBankCards} />
            <MenuItem icon={Shield} label="实名认证" sub="已认证" onClick={onRealNameAuth} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-200">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <span className="text-xs font-medium text-slate-500">交易管理</span>
          </div>
          <div className="divide-y divide-slate-100">
            {userRole === 'supplier' && (
              <MenuItem icon={Receipt} label="反向开票记录" badge="2" onClick={onViewInvoiceRecords} />
            )}
            {userRole === 'organizer' && (
              <MenuItem icon={Users} label="实供人管理" badge="3" onClick={onManageSuppliers} />
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-slate-200">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <span className="text-xs font-medium text-slate-500">其他</span>
          </div>
          <div className="divide-y divide-slate-100">
            <MenuItem icon={HelpCircle} label="帮助与反馈" />
          </div>
        </div>

        <button className="w-full bg-white text-red-500 py-3.5 rounded-xl shadow-sm font-medium border border-slate-200 hover:bg-red-50 transition-colors flex items-center justify-center gap-2">
          <LogOut size={18} />
          退出登录
        </button>
      </div>
    </div>
  );
}
